import { createApi } from '@reduxjs/toolkit/query/react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Feature, View } from 'models/views';
import { SegmentationParams, FramesPage, PredictionParams } from 'models/segmentation';
import { baseQueryWithAuthRedirect } from './rtk';
import { ValidateTokenResponse } from 'models/auth';
import type { AppDispatch } from '../store';

interface SegmentClassParams {
  view?: string;
  forceRefetch?: number;
}

export interface ReviewFramePreviewParams {
  dicomIcid: string;
  frameIndex: number;
  includeEmptyFrame?: boolean;
}

export interface CreateSegmentationLabelPayload {
  uuid?: string;
  frame: string;
  segmentationClass: string;
  paths: string[];
  isFullyVisible: boolean;
  sourceUuid: string | null;
  sourceType: 'SEGMENTATION_LABEL' | null;
}

export const viewsAPI = createApi({
  reducerPath: 'viewsAPI',
  baseQuery: baseQueryWithAuthRedirect,
  tagTypes: ['Views', 'Features', 'Frames'],
  endpoints: builder => ({
    getViews: builder.query<View[], string>({
      query: () => ({
        url: 'api/v2/view-classes/',
        params: {
          paginate: false,
        },
      }),
      providesTags: [
        { type: 'Views', id: 'current' },
        { type: 'Features', id: 'current' },
      ],
    }),
    createViewLabel: builder.mutation<any, { dicom: string; view_class: string; icid?: string }>({
      query: payload => ({
        url: 'api/v2/view-labels/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Views', id: 'current' }],
    }),
    createSegmentationLabel: builder.mutation<unknown, CreateSegmentationLabelPayload>({
      query: payload => ({
        url: 'api/v1/segmentation-labels/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'Frames', id: 'LIST' },
        { type: 'Frames', id: 'STATS' },
      ],
    }),
    getSegmentationClasses: builder.query<Feature[], SegmentClassParams>({
      query: params => ({
        url: params.view
          ? `api/v1/segmentation-classes/${params.view}`
          : 'api/v1/segmentation-classes/',
      }),
      providesTags: [
        { type: 'Views', id: 'current' },
        { type: 'Features', id: 'current' },
      ],
    }),
    // New: keypoint collection classes
    getKeypointCollectionClasses: builder.query<Feature[], void>({
      query: () => ({
        url: 'api/v1/keypoint-collection-classes/',
      }),
      providesTags: [
        { type: 'Views', id: 'current' },
        { type: 'Features', id: 'current' },
      ],
    }),
    getFrames: builder.query<FramesPage, SegmentationParams>({
      query: (args: SegmentationParams) => {
        // Convert filters to query params for GET
        const params = new URLSearchParams();
        params.append('page', String(args.page));
        params.append('size', String(args.size));
        if (args.view && args.view.uuid) params.append('view_class', args.view.uuid);
        if (args.features && args.features.length > 0) {
          args.features.forEach(f => params.append('segmentation_class', f.uuid));
        }
        if (args.includeAllSegmentationLabels)
          params.append('include_all_segmentation_labels', 'true');
        if (args.advancedFilters) {
          if (args.advancedFilters.datasources && args.advancedFilters.datasources.uuid)
            params.append('datasource', args.advancedFilters.datasources.uuid);
          if (args.advancedFilters.dicomICID)
            params.append('dicom', args.advancedFilters.dicomICID);
          if ((args.advancedFilters as any).frameId)
            params.append('frame_id', (args.advancedFilters as any).frameId);
          if (
            (args.advancedFilters as any).reviewStatus &&
            (args.advancedFilters as any).reviewStatus.length > 0
          )
            params.append('review_status', (args.advancedFilters as any).reviewStatus.join(','));
          if (
            args.advancedFilters.study &&
            typeof args.advancedFilters.study === 'string' &&
            args.advancedFilters.study.trim()
          ) {
            params.append('study', args.advancedFilters.study);
          }
          if (args.advancedFilters.segmentationICID)
            params.append('segmentation_icid', args.advancedFilters.segmentationICID);
          if (args.advancedFilters.technician)
            params.append('user', args.advancedFilters.technician);
          if (args.advancedFilters.created_at_after)
            params.append('created_at_after', args.advancedFilters.created_at_after);
          if (args.advancedFilters.created_at_before)
            params.append('created_at_before', args.advancedFilters.created_at_before);
          if (args.advancedFilters.updated_at_after)
            params.append('updated_at_after', args.advancedFilters.updated_at_after);
          if (args.advancedFilters.updated_at_before)
            params.append('updated_at_before', args.advancedFilters.updated_at_before);
          // Annotation set name (for segmentation labels, keypoints, and view labels)
          if (
            args.advancedFilters.annotationSetName &&
            typeof args.advancedFilters.annotationSetName === 'string' &&
            args.advancedFilters.annotationSetName.trim()
          ) {
            params.append('annotation_set_name', args.advancedFilters.annotationSetName);
          }
        }
        return {
          url: `api/v1/segmentation-label-frames/?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'Frames', id: 'LIST' }],
    }),
    getReviewFramePreview: builder.query<FramesPage, ReviewFramePreviewParams>({
      query: ({ dicomIcid, frameIndex, includeEmptyFrame }) => ({
        url: 'api/v1/segmentation-label-frames/',
        params: {
          page: 1,
          size: 1,
          frame_id: `${dicomIcid}_${frameIndex}`,
          include_all_segmentation_labels: true,
          ...(includeEmptyFrame ? { include_empty_frame: true } : {}),
        },
      }),
    }),
    // Filtered frames stats endpoint (uses same filters as getFrames)
    getFramesStats: builder.query<any, SegmentationParams>({
      query: (args: SegmentationParams) => {
        // Build same query params as getFrames so backend applies identical filters
        const params = new URLSearchParams();
        params.append('page', String(args.page));
        params.append('size', String(args.size));
        if (args.view && args.view.uuid) params.append('view_class', args.view.uuid);
        if (args.features && args.features.length > 0) {
          args.features.forEach(f => params.append('segmentation_class', f.uuid));
        }
        if (args.includeAllSegmentationLabels)
          params.append('include_all_segmentation_labels', 'true');
        if (args.advancedFilters) {
          if (args.advancedFilters.datasources && args.advancedFilters.datasources.uuid)
            params.append('datasource', args.advancedFilters.datasources.uuid);
          if (args.advancedFilters.dicomICID)
            params.append('dicom', args.advancedFilters.dicomICID);
          if ((args.advancedFilters as any).frameId)
            params.append('frame_id', (args.advancedFilters as any).frameId);
          if (
            (args.advancedFilters as any).reviewStatus &&
            (args.advancedFilters as any).reviewStatus.length > 0
          )
            params.append('review_status', (args.advancedFilters as any).reviewStatus.join(','));
          if (
            args.advancedFilters.study &&
            typeof args.advancedFilters.study === 'string' &&
            args.advancedFilters.study.trim()
          ) {
            params.append('study', args.advancedFilters.study);
          }
          if (args.advancedFilters.segmentationICID)
            params.append('segmentation_icid', args.advancedFilters.segmentationICID);
          if (args.advancedFilters.technician)
            params.append('user', args.advancedFilters.technician);
          if (args.advancedFilters.created_at_after)
            params.append('created_at_after', args.advancedFilters.created_at_after);
          if (args.advancedFilters.created_at_before)
            params.append('created_at_before', args.advancedFilters.created_at_before);
          if (args.advancedFilters.updated_at_after)
            params.append('updated_at_after', args.advancedFilters.updated_at_after);
          if (args.advancedFilters.updated_at_before)
            params.append('updated_at_before', args.advancedFilters.updated_at_before);
          if (
            args.advancedFilters.annotationSetName &&
            typeof args.advancedFilters.annotationSetName === 'string' &&
            args.advancedFilters.annotationSetName.trim()
          ) {
            params.append('annotation_set_name', args.advancedFilters.annotationSetName);
          }
        }
        return {
          url: `api/v1/segmentation-label-frames/stats/?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'Frames', id: 'STATS' }],
    }),
    // New: prediction frames endpoint
    getPredictionFrames: builder.query<FramesPage, PredictionParams>({
      query: (args: PredictionParams) => {
        const params = new URLSearchParams();
        params.append('page', String(args.page));
        params.append('size', String(args.size));
        if (args.view && args.view.uuid) params.append('view_class', args.view.uuid);
        if (args.features && args.features.length > 0) {
          args.features.forEach(f => params.append('segmentation_class', f.uuid));
        }
        if (args.advancedFilters) {
          if (args.advancedFilters.datasources && args.advancedFilters.datasources.uuid)
            params.append('datasource', args.advancedFilters.datasources.uuid);
          if (args.advancedFilters.dicomICID)
            params.append('dicom', args.advancedFilters.dicomICID);
          if ((args.advancedFilters as any).frameId)
            params.append('frame_id', (args.advancedFilters as any).frameId);
          if (
            (args.advancedFilters as any).reviewStatus &&
            (args.advancedFilters as any).reviewStatus.length > 0
          )
            params.append('review_status', (args.advancedFilters as any).reviewStatus.join(','));
          if (
            args.advancedFilters.study &&
            typeof args.advancedFilters.study === 'string' &&
            args.advancedFilters.study.trim()
          ) {
            params.append('study', args.advancedFilters.study);
          }
          if (args.advancedFilters.segmentationICID)
            params.append('segmentation_icid', args.advancedFilters.segmentationICID);
          if (args.advancedFilters.keypointICID)
            params.append('keypoint_icid', args.advancedFilters.keypointICID);
          if (args.advancedFilters.technician && args.advancedFilters.technician.length > 0) {
            args.advancedFilters.technician.forEach(t => params.append('user', t.uuid));
          }
          if (args.advancedFilters.created_at_after)
            params.append('created_at_after', args.advancedFilters.created_at_after);
          if (args.advancedFilters.created_at_before)
            params.append('created_at_before', args.advancedFilters.created_at_before);
          if (args.advancedFilters.updated_at_after)
            params.append('updated_at_after', args.advancedFilters.updated_at_after);
          if (args.advancedFilters.updated_at_before)
            params.append('updated_at_before', args.advancedFilters.updated_at_before);
          if (
            args.advancedFilters.source_name &&
            typeof args.advancedFilters.source_name === 'string' &&
            args.advancedFilters.source_name.trim()
          ) {
            params.append('source_name', args.advancedFilters.source_name);
          }
        }
        return {
          url: `api/v1/segmentation-prediction-frames/?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'Frames', id: 'PREDICTION_LIST' }],
    }),

    validateToken: builder.query<ValidateTokenResponse, string>({
      query: (token: string) => ({
        url: 'auth/validate',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    }),
  }),
});

export const usePrefetchReviewFramePreview = () => {
  const dispatch = useDispatch<AppDispatch>();

  return useCallback(
    (params: ReviewFramePreviewParams) =>
      dispatch(
        viewsAPI.endpoints.getReviewFramePreview.initiate(params, { subscribe: false })
      ).unwrap(),
    [dispatch]
  );
};

export const {
  useGetViewsQuery,
  useGetSegmentationClassesQuery,
  useLazyGetSegmentationClassesQuery,
  useGetFramesQuery,
  useGetFramesStatsQuery,
  useValidateTokenQuery,
  useGetKeypointCollectionClassesQuery,
  useGetPredictionFramesQuery,
  useGetReviewFramePreviewQuery,
  useCreateViewLabelMutation,
  useCreateSegmentationLabelMutation,
} = viewsAPI;
